import { Action, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import df from 'dateformat';
import {
  eqBy, last, prop, propOr, sort, unionWith,
} from 'ramda';
import { combineEpics, StateObservable } from 'redux-observable';
import {
  EMPTY, from, of, concat, Observable, forkJoin,
} from 'rxjs';
import {
  catchError, debounceTime, filter, isEmpty, map, mergeMap, pluck, switchMap,
} from 'rxjs/operators';

import { logClientError } from '@ezugi/bootstrap/src/util/logUtils';
import { AMOUNT_TYPES, ULTIMATE_DICE_ID } from '@ezugi/constants';
import { GenericPayloadType, PlayerHistoryRound } from '@ezugi/types';
import { formatAmount, handleError } from '@ezugi/utils';

import { GAME_ID_TO_SLUGS_MAP } from '../../../constants/game';
import { getPayoutsForGameId } from '../../../util/getPayouts';
import { notificationActions, playerHistoryActions } from '../../actions';
import {
  authSelector,
  currentLanguageSelector,
  formatSelector,
  gamesLocaleSelector,
  gameTypesSelector,
} from '../../selectors';
import { localeSelector } from '../../selectors/locale';
import { playerHistorySelector, playerHistoryPayoutsSelector } from '../../selectors/playerHistory';
import { RootState } from '../../types';
import {
  getDayRoundsHistory, getPlayerHistory, getRoundDetails, parsersMap,
} from './utils';

const { playerHistory, roundDetails } = playerHistoryActions;
const { notification } = notificationActions;

const descByDate = (
  a: PlayerHistoryRound,
  b: PlayerHistoryRound
) => new Date(b._date).getTime() - new Date(a._date).getTime();

const toISODate = (date: string, isDay?: boolean) => (isDay
  ? date.replace(/-/g, '/') : `${date.replace(/\s/, 'T')}.000Z`);

const parseDateTime = (dateTime: string, dateFormat: string, isDay = false) => {
  const dateObj = new Date(toISODate(dateTime ?? '', isDay));
  return {
    key: df(dateObj, 'yyyy-mm-dd'),
    day: df(dateObj, 'dddd'), // Day of the week as its full name.
    dayNo: df(dateObj, 'dd'), // Day of the month as digits; leading zero for single-digit days.
    month: df(dateObj, 'mmmm'), // Month as a three-letter abbreviation.
    date: df(dateObj, dateFormat, true), // convert local time to UTC
    time: df(dateObj, 'HH:MM:ss', true), // convert local time to UTC
  };
};

const localTimeWithTimezoneOffset = (date = new Date()) => {
  const localTime = date.getTime();
  const localOffset = date.getTimezoneOffset() * 60 * 1000; // timezone offset in milliseconds
  return Math.floor((localTime - localOffset) / 1000); // local time seconds since epoch time
};

const onHistoryError = (error: Error) => concat(
  of(notification.add({ message: 'notifications.failed_getting_history' })),
  handleError({
    error,
    failAction: playerHistory.failure as unknown as ActionCreatorWithPayload<GenericPayloadType>,
    options: {
      clientErrorHandler: logClientError,
    },
  })
);

type BetInfo = {
  bet: string;
  win: string;
  date: string;
  day: any;
  rounds: string;
  SessionCurrency: string;
  round_id: string;
  table_id: string;
  game_id: string;
  currency: string;
  roundId: any;
};

const playerHistoryEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(playerHistory.request.match),
  /* eslint-disable camelcase */
  switchMap(() => {
    const { identifier } = authSelector(state$.value) as { identifier: string };
    const { lastRequestTime, expandedDay } = playerHistorySelector(state$.value);
    const { dateFormat = 'yyyy-mm-dd', ...format } = formatSelector(state$.value) as { dateFormat: string };

    return from(
      getPlayerHistory({
        identifier,
        time_period: lastRequestTime ? Date.now() / 1000 - lastRequestTime : 60 * 60 * 24 * 180 /* 180 days */,
        current_time: localTimeWithTimezoneOffset(), // local time in seconds
      })
    ).pipe(
      mergeMap(({ result: daysHistory = [] }: { result: any }) => {
        if (daysHistory.length === 0) { // no history
          return of(playerHistory.success(undefined));
        }

        const newEntries = daysHistory.map((e: BetInfo, i: number) => ({
          index: i,
          _bet: +e.bet,
          _win: +e.win,
          _date: e.date,
          roundsNumber: +e.rounds,
          ...parseDateTime(e.day, dateFormat, true),
          bet: formatAmount(e.bet, AMOUNT_TYPES.BET, format, e.SessionCurrency),
          win: formatAmount(e.win, AMOUNT_TYPES.BET, format, e.SessionCurrency),
          currency: e.SessionCurrency ?? '',
          rounds: [],
        }));

        const { totalBet, totalWin, totalRoundsPlayed } = newEntries.reduce(
          (acc: Record<string, number>, { _bet = 0, _win = 0, roundsNumber = 0 }) => ({
            totalBet: acc.totalBet + _bet,
            totalWin: acc.totalWin + _win,
            totalRoundsPlayed: acc.totalRoundsPlayed + roundsNumber,
          }),
          { totalBet: 0, totalWin: 0, totalRoundsPlayed: 0 }
        );

        return of(
          playerHistory.success({
            days: newEntries.reduce((acc: Record<string, any>, curr: any) => ({ ...acc, [curr.key]: curr }), {}),
            totalBet: formatAmount(totalBet, AMOUNT_TYPES.BET, format, newEntries[0]?.currency),
            totalWin: formatAmount(totalWin, AMOUNT_TYPES.BET, format, newEntries[0]?.currency),
            played: totalRoundsPlayed,
            lastRequestTime: Date.now() / 1000,
          }),
          ...(expandedDay ? [ playerHistory.requestRounds({ day: expandedDay }) ] : []),
        );
      }),
      filter(isEmpty as any),
      catchError(onHistoryError)
    );
  })
);

const roundsHistoryEpic = (
  action$: Observable<ActionCreatorWithPayload<{ day: string, loadMore?: boolean }>>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(playerHistory.requestRounds.match),
  pluck('payload'),
  debounceTime(300),
  switchMap(({ day, loadMore = false }: { day: string, loadMore?: boolean }) => {
    const state = state$.value;
    const { identifier } = authSelector(state) as { identifier: string };
    const history = playerHistorySelector(state);
    const gameTypes = gameTypesSelector(state) as unknown as Record<number, any>;
    const gameLocales = gamesLocaleSelector(state) || {};
    const { dateFormat = 'yyyy-mm-dd', ...format } = formatSelector(state,) as { dateFormat: string };

    const prevRounds: PlayerHistoryRound[] = propOr([], day, history.rounds);
    const oldestRoundParam = loadMore ? { oldest_round_id: last(prevRounds)?.roundId ?? '' } : {};

    return from(
      getDayRoundsHistory({
        identifier,
        day,
        ...oldestRoundParam,
        current_time: localTimeWithTimezoneOffset(), // local time in seconds
      }),
    ).pipe(
      mergeMap(
        ({
          result: { player_history: roundsHistory = [] } = {},
        }: any) => {
          if (roundsHistory.length === 0) {
            return EMPTY;
          }

          const entries = roundsHistory.map((e: BetInfo, i: number) => {
            const gameType = gameTypes[+e.game_id] ?? {};
            return {
              index: i,
              _bet: e.bet,
              _win: e.win,
              _date: e.date,
              ...parseDateTime(e.date, dateFormat),
              roundId: e.round_id,
              gameId: +e.game_id,
              tableId: e.table_id,
              // replace added for https://ezugi1.atlassian.net/browse/ERD-17162
              gameName: (gameLocales[`${gameType.name}_${e.table_id}`]
                      || gameLocales[gameType.name]
                      || gameType.serverName
                      || '').replace('Ez', 'EZ '),
              bet: formatAmount(e.bet, AMOUNT_TYPES.BET, format, e.currency,),
              win: formatAmount(e.win, AMOUNT_TYPES.BET, format, e.currency,),
              currency: e.currency ?? '',
            };
          });

          // combine old rounds list with new rounds list
          const newRounds = unionWith(eqBy(prop('roundId')), entries, prevRounds);
          return of(
            playerHistory.success({
              rounds: {
                ...history.rounds,
                [day]: sort(descByDate, newRounds),
              },
              expandedDay: day,
            })
          );
        },
      ),
      catchError(onHistoryError),
    );
  }),
);

/** list of games that need payouts info in player history round details */
const gameIdsNeedingPayouts = [ ULTIMATE_DICE_ID ];

const roundDetailsEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(roundDetails.request.match),
  map((action) => action.payload),
  filter(({ roundId }): boolean => {
    const { roundDetails: { [roundId]: existing } = {} } = playerHistorySelector(state$.value);
    const { code: languageCode } = currentLanguageSelector(state$.value);
    // @ts-expect-error
    return !existing || existing.currentLanguage !== languageCode || !!existing.error;
  }),
  map((payload) => {
    const { identifier } = authSelector(state$.value) as { identifier: string };
    return {
      ...payload,
      identifier,
      current_time: localTimeWithTimezoneOffset(), // local time in seconds
    };
  }),
  switchMap((payload) => forkJoin([
    from(getRoundDetails(payload)),
    gameIdsNeedingPayouts.includes(payload.gameId) && !playerHistoryPayoutsSelector(state$.value)[payload.gameId]
      ? from(getPayoutsForGameId(state$.value, payload.gameId))
      : Promise.resolve(undefined),
    from(parsersMap[GAME_ID_TO_SLUGS_MAP[payload.gameId]]()),
  ]).pipe(
    mergeMap(([ { result: roundDetailsResponse, ...roundData }, payoutsResponse, { default: parserFn } ]) => {
      if (!roundData) {
        return of(roundDetails.failure({
          [payload.roundId]: {
            error: true,
          },
        }));
      }

      const parsedPayouts = payoutsResponse
        ? payoutsResponse.result.payouts.map((payout) => Number(JSON.parse(payout.payout).option1))
        : undefined;

      const { dateFormat = 'yyyy-mm-dd', ...format } = formatSelector(state$.value) as { dateFormat: string };
      const { code: languageCode } = currentLanguageSelector(state$.value);

      const locale = localeSelector(state$.value);
      const payouts = parsedPayouts
        ? { [payload.gameId]: parsedPayouts }
        : playerHistoryPayoutsSelector(state$.value);

      const formattedRoundData = {
        ...roundData,
        formattedStartBalance: formatAmount(
          roundData.startBalance,
          AMOUNT_TYPES.BET,
          format,
          roundData.currency
        ),
        formattedEndBalance: formatAmount(roundData.endBalance, AMOUNT_TYPES.BET, format, roundData.currency),
        startDateTime: df(toISODate(roundData.startDateTime), `${dateFormat} HH:MM:ss`, true),
        endDateTime: df(toISODate(roundData.endDateTime), `${dateFormat} HH:MM:ss`, true),
        currentLanguage: languageCode,
        ...parserFn({ result: roundDetailsResponse, dateFormat, amountFormat: format, locale, payouts }),
      };

      return of(
        roundDetails.success({ [payload.roundId]: formattedRoundData }),
        ...(parsedPayouts ? [ playerHistory.setPayouts({ [payload.gameId]: parsedPayouts }) ] : []),
      );
    }),
    catchError(() => of(
      roundDetails.failure({
        [payload.roundId]: {
          error: true,
        },
      })
    ))
  ))
);

export default combineEpics(playerHistoryEpic, roundsHistoryEpic, roundDetailsEpic);
